import {
  CloseOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useLoadScript } from '@react-google-maps/api'
import type { GetProp, UploadFile, UploadProps } from 'antd'
import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Switch,
  Tooltip,
  Typography,
  Upload,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import countries from 'constants/country'
import { logError, logInfo } from 'utils/logger'

import './style.scss'

const { Title } = Typography

const DefaultLocation = { lat: 10, lng: 106 }

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface PropertyItem {
  id: number
  value: string
}

interface PropertyType {
  id: string
  name: string
  properties: { key: number; name: string }[]
}

export default function CreateP2P() {
  const [form] = Form.useForm()
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [, setSelectedPropertyType] = useState<string | undefined>(undefined)
  const [propertyFields, setPropertyFields] = useState<
    { key: number; name: string; value: string }[]
  >([])
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [uploading] = useState(false)
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [singleFile, setSingleFile] = useState<UploadFile[]>([])
  const [defaultLocation] = useState(DefaultLocation)
  const [, _setLocation] = useState(defaultLocation)
  const [latitude, setLatitude] = useState(10.762622)
  const [longitude, setLongitude] = useState(106.660172)
  const [address, setAddress] = useState<string>('')
  const navigate = useNavigate()

  const _fetchLocationData = async (lat: number, lng: number) => {
    const response = await fetch(
      `https://api.opencagedata.com/geocode/v1/json?q=${lat}+${lng}&key=9a1cca33c5e04e6f82cd76e4c21588c8`
    )
    const data = await response.json()
    const { components } = data.results[0]
    const country = components.country
    const city = components.city || components.state
    if (country && city) {
      const locationName = country + ' ' + city
      setAddress(locationName)
    }
    const copyFields = [...propertyFields]
    copyFields.map((item) => {
      if (item.name === 'Location') item.value = country + ' ' + city
    })
    form.setFieldValue('Location', country + ' ' + city)
    setPropertyFields(copyFields)
  }

  const handleUploadSingle = async (info: any) => {
    const { file } = info
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await AppAdmin.uploadSingleFile(formData)
      if (response.data.success) {
        message.success('File uploaded successfully')
        setThumbnail(response.data.data.filePath)
        form.setFields([{ name: 'uploadSingle', errors: [] }])
      }
    } catch (error: any) {
      message.error(error?.message || 'File upload failed')
      logError('Upload error:', error)
    }
  }

  const handleUploadMulti = async () => {
    const formData = new FormData()
    fileList.forEach((file) => {
      formData.append('files', file as FileType)
    })
    try {
      const response = await AppAdmin.uploadMultipleFiles(formData)
      message.success('Files uploaded successfully')
      if (response.data.success) {
        const list = response.data.data.map((item: any) => item.filePath)
        const updatedList = [...thumbnails, ...list]
        setThumbnails(updatedList)
        form.setFields([{ name: 'upLloadMulti', errors: [] }])
      }
    } catch (error: any) {
      message.error(error?.message || 'Files upload failed')
      logError('Upload error:', error)
    } finally {
      setFileList([])
    }
  }

  const locationURL = useLocation()
  const params = new URLSearchParams(locationURL.search)
  const code = params.get('code')

  const handleGetData = useCallback(async () => {
    try {
      if (code) {
        const response = await AppAdmin.getPortalDetails(code)
        if (response?.data?.success) {
          const details = response?.data?.data
          const propertyValues = details.properties.reduce(
            (acc: Record<string, string>, item: PropertyItem) => {
              acc[`property_${item.id}`] = item.value
              return acc
            },
            {}
          )
          setThumbnails(details.thumbnails)
          const res = await AppAdmin.getListProperty()
          let types = []
          if (res?.data.success) {
            types = res?.data.data.map((item: PropertyType) => ({
              ...item,
              id: Number(item.id),
            }))
            setPropertyTypes(types)
          }
          const selectedType: any = types.find((type: any) => type.name === details.typeOfAsset)
          const country = countries.find((item) => item.name === details?.country)
          const formValues = {
            code,
            name: details?.name,
            type: selectedType?.id,
            country: country?.code,
            contractAddress: details?.contractAddress,
            apy: details?.apy,
            description: details?.description,
            status: details?.status === 1,
            investmentPeriod: 0,
            ...propertyValues,
          }
          form.setFieldsValue(formValues)
          if (selectedType) {
            const propertiesWithValues = selectedType.properties.map((prop: any) => {
              const matchedProperty = details.properties.find(
                (item: PropertyItem) => item.id === prop.key
              )
              return {
                key: prop.key,
                name: prop.name,
                value: matchedProperty ? matchedProperty.value : '',
              }
            })
            logInfo(propertiesWithValues)
            setPropertyFields(propertiesWithValues)
          }
        }
      } else {
        const res = await AppAdmin.getListProperty()
        let types = []
        if (res?.data.success) {
          types = res?.data.data.map((item: PropertyType) => ({
            ...item,
            id: Number(item.id),
          }))
          setPropertyTypes(types)
        }
      }
    } catch (error: any) {
      logError('Upload error:', error)
    }
  }, [code, form])

  useEffect(() => {
    handleGetData()
  }, [code, handleGetData])

  const handleApprove = async (code: string, type: string, approve: number) => {
    try {
      await AppAdmin.approveRequest({
        code,
        type,
        approve,
      })
      message.success('Success to approve request!')
    } catch (error: any) {
      logError(error)
      message.error(error?.message || 'Fail to approve request!')
    }
  }

  const beforeUploadSingle = async (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      form.setFields([{ name: 'uploadSingle', errors: ['You can only upload JPG/PNG file!'] }])
      return false
    }
    return true
  }

  const beforeUploadMulti = async (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      form.setFields([{ name: 'upLloadMulti', errors: ['You can only upload JPG/PNG file!'] }])
    } else {
      setFileList([...fileList, file])
    }

    return false
  }

  const props: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file)
      const newFileList = fileList.slice()
      newFileList.splice(index, 1)
      setFileList(newFileList)
    },
    fileList,
  }

  const handleRemoveImage = (indexToRemove: any) => {
    const updatedThumbnails = thumbnails.filter((_, index) => index !== indexToRemove)
    setThumbnails(updatedThumbnails)
  }

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      const {
        type,
        name,
        country,
        investmentPeriod,
        contractAddress,
        description = '',
        latitude,
        longitude,
        status,
      } = values
      const propertyItems = propertyFields.map((item) => ({
        id: Number(item.key),
        value: item.value,
      }))
      await AppAdmin.p2pCreate({
        type: Number(type),
        fullName: name,
        country: country,
        investmentPeriod: investmentPeriod || 10,
        contractAddress: contractAddress,
        description: description,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        propertyItems: propertyItems,
        latitude: latitude,
        longitude: longitude,
        status: status,
      })
      if (code) {
        await handleApprove(code, 'LENDING', 2)
      }
      message.success('P2P lending created successfully!')
      navigate('/p2plending')
    } catch (error: any) {
      setShowInfo(false)
      message.error(error?.message || 'Error while submitting form')
      logError('Error while submitting form:', error)
    }
  }

  const handleCancel = () => {
    navigate('/p2plending')
  }

  const [, setShowLocation] = useState(false)

  const handlePropertyTypeChange = (value: string) => {
    const selectedType = propertyTypes.find((type) => type.id === value)
    if (selectedType) {
      setShowLocation(true)
      setSelectedPropertyType(value)
      const propertiesWithValues = selectedType.properties.map((prop) => ({
        key: prop.key,
        name: prop.name,
        value: prop.name === 'Location' && address.length > 0 ? address : '',
      }))
      setPropertyFields(propertiesWithValues)
    } else {
      setShowLocation(false)
    }
  }

  const [infoContract, setInfoContract] = useState<any>({})
  const [showInfo, setShowInfo] = useState(false)

  const getInfoContract = async (contractAddress: string) => {
    try {
      if (contractAddress) {
        const res = await AppAdmin.getInfoContract(contractAddress)
        if (res?.data.success) {
          setInfoContract(res?.data.data)
          message.success('Get contract address info success!')
        }
      }
    } catch (error: any) {
      message.error(error?.message || 'Contract address invalid')
      setInfoContract({})
      logError('Error: ', error)
    }
  }

  const additionalItems = [BREADCRUMBS.P2P, BREADCRUMBS.P2P_CREATE_NO_LINK]

  const [, setCenter] = useState<any>({ lat: 10.8231, lng: 106.6297 })

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyDrgqN55Yu90gPGHJbbaWIDvctWYo86cEc',
  })

  if (!isLoaded) return <div>Loading...</div>

  return (
    <div className="p2p-create">
      <Breadcrumb additionalItems={additionalItems} />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ remember: true }}
        style={{ padding: '20px 10px' }}
        onFinish={handleOk}
      >
        <fieldset>
          <legend>Basic Information</legend>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Name
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending name, please enter your chosen name as text
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="name"
                rules={[
                  { required: true, message: 'Please input the name!' },

                  {
                    min: 3,
                    max: 50,
                    message: 'Name should be between 3 and 50 characters!',
                  },
                ]}
              >
                <Input placeholder="Enter the name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Status
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to manage your P2P lending status. When the switch is{' '}
                            <strong>ON</strong>, the lending is active. When the switch is{' '}
                            <strong>OFF</strong>, the lending is inactive.
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="status"
                required
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="country"
                label={
                  <>
                    Country
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending country, please select your chosen country
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                required
              >
                <Select showSearch optionFilterProp="children" placeholder="Select a country">
                  {countries.map((country) => (
                    <Select.Option key={country.code} value={country.code}>
                      {country.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                style={{ display: 'none' }}
                label={
                  <>
                    Investment Period
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending investment period, please enter your chosen
                            investment period as number
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="investmentPeriod"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please input the investment period!",
                //   },
                // ]}
              >
                <Input type="number" placeholder="Enter the period in months" />
              </Form.Item>
            </Col>
            <Col style={{ marginBottom: 12 }} span={12}>
              <Form.Item
                label={
                  <>
                    Pool Contract Address
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your p2p lending contract address, please enter the correct pool
                            contract address and check its information
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="contractAddress"
                rules={[
                  {
                    required: true,
                    message: 'Please input the contract address!',
                  },
                ]}
                style={{ marginBottom: 12 }}
              >
                <Input placeholder="Enter the contract address" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Button
                style={{ marginTop: 30 }}
                htmlType="button"
                onClick={() => {
                  if (showInfo) {
                    setShowInfo(!showInfo)
                  } else {
                    setShowInfo(!showInfo)
                    getInfoContract(form.getFieldValue('contractAddress'))
                  }
                }}
              >
                {!showInfo ? 'View Info' : 'Hide Info'}
              </Button>
            </Col>
            <Col span={24}>
              {infoContract && showInfo && (
                <div className="info-contract">
                  <Title style={{ marginTop: 0 }} level={4}>
                    Contract Information
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending address information, check the data below
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <InfoCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </Title>
                  <Descriptions bordered column={1} style={{ marginBottom: '16px' }}>
                    <Descriptions.Item label="APY">{infoContract.apy}</Descriptions.Item>
                    <Descriptions.Item label="Boots APY">
                      {infoContract.boots_apy}
                    </Descriptions.Item>
                    <Descriptions.Item label="Funding Target">
                      {infoContract.fundingTarget}
                    </Descriptions.Item>
                    <Descriptions.Item label="Start Time">
                      {infoContract.startDate}
                    </Descriptions.Item>
                    <Descriptions.Item label="End Time">{infoContract.endDate}</Descriptions.Item>
                  </Descriptions>
                </div>
              )}
            </Col>
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Description
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending description, please enter your chosen
                            description as text
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="description"
                rules={[
                  { required: true, message: 'Please input the description!' },
                  {
                    min: 3,
                    max: 500,
                    message: 'Description should be between 3 and 500 characters!',
                  },
                ]}
              >
                <Input.TextArea rows={4} placeholder="Enter the description" />
              </Form.Item>
            </Col>
          </Row>
        </fieldset>
        <fieldset>
          <legend>Property Information</legend>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Property Type
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending property type. Please select your chosen
                            property. After selecting a property, a map will appear for you to pick
                            the location
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="type"
                rules={[{ required: true, message: 'Please select the type!' }]}
              >
                <Select placeholder="Select property type" onChange={handlePropertyTypeChange}>
                  {propertyTypes.map((type) => (
                    <Select.Option key={type.id} value={type.id}>
                      {type.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {/* {showLocation && (
            <Col
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 40,
                marginBottom: 40,
              }}
              span={24}
            >
              <div style={{ height: "300px", width: "500px" }}>
                <GoogleMap
                  mapContainerStyle={{ width: '500px', height: '300px' }}
                  center={center}
                  zoom={zoom}
                  onClick={(e) => {
                    const { latLng } = e;
                    setCenter({ lat: latLng?.lat(), lng: latLng?.lng() });
                    handleChangeLocation(latLng?.lat(), latLng?.lng());
                  }}
                >
                  <Marker position={center} />
                </GoogleMap>
              </div>
            </Col>
          )} */}
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Latitude
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending location coordinates (latitude). You can get
                            the value by entering your chosen latitude as number
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="latitude"
              >
                <Input
                  type="number"
                  min={-90}
                  max={90}
                  placeholder="Enter latitude"
                  value={latitude}
                  step={'any'}
                  onChange={(e) => {
                    setLatitude(parseFloat(e.target.value))
                    setCenter({ lat: parseFloat(e.target.value), lng: longitude })
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Longitude
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your P2P lending location coordinates (longitude). You can get
                            the value by entering your chosen longitude as number
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="longitude"
              >
                <Input
                  type="number"
                  min={-180}
                  max={180}
                  placeholder="Enter longitude"
                  value={longitude}
                  step={'any'}
                  onChange={(e) => {
                    setLongitude(parseFloat(e.target.value))
                    setCenter({ lat: latitude, lng: parseFloat(e.target.value) })
                  }}
                />
              </Form.Item>
            </Col>
            {propertyFields.map((field) => {
              const isNumberField =
                // field.name === "Area" ||
                // field.name === "Property Value" ||
                // field.name === "Price Per SQM" ||
                field.name === 'Beds' ||
                field.name === 'Baths' ||
                field.name === 'Year Built' ||
                field.name === 'Floors'
              // field.name === "Annual Gross Rental Yield" ||
              // field.name === "Annual Asset Appreciation" ||
              // field.name === "Total Projected Gross Yield";

              return (
                <Col span={12} key={field.key}>
                  <Form.Item
                    name={`property_${field.key}`}
                    label={
                      <>
                        {field.name}
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending {field.name} property, please enter your
                                chosen {field.name} as {isNumberField ? 'number' : 'text'}
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                  >
                    {isNumberField ? (
                      <InputNumber
                        style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
                        addonAfter={
                          field.name === 'Annual Gross Rental Yield' ||
                          field.name === 'Annual Asset Appreciation' ||
                          field.name === 'Total Projected Gross Yield'
                            ? '%'
                            : field.name === 'Property Value' || field.name === 'Price Per SQM'
                              ? '$'
                              : field.name === 'Area'
                                ? `m\u00B2`
                                : undefined
                        }
                        min={0}
                        step={1}
                        // max={field.name === "Annual Gross Rental Yield" ||
                        //   field.name === "Annual Asset Appreciation" ||
                        //   field.name === "Total Projected Gross Yield" ? 100 : undefined}
                        // formatter={(value: any) =>
                        //   (field.name === "Beds" ||
                        //     field.name === "Baths" ||
                        //     // field.name === "Year Built" ||
                        //     field.name === "Floors") ? value :
                        //     `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        // parser={(value: any) =>
                        //   field.name === "Beds" ||
                        //     field.name === "Baths" ||
                        //     // field.name === "Year Built" ||
                        //     field.name === "Floors" ? value :
                        //     value?.replace(/\$\s?|(,*)/g, '') as unknown as number}
                        value={Number(field.value)}
                        onChange={(value: any) => {
                          const newFields = [...propertyFields]
                          const index = newFields.findIndex((f) => f.key === field.key)
                          newFields[index].value = value
                          setPropertyFields(newFields)
                        }}
                        placeholder={`Enter ${field.name}`}
                      />
                    ) : (
                      <Input
                        style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                        value={field.value}
                        addonAfter={
                          field.name === 'Annual Gross Rental Yield' ||
                          field.name === 'Annual Asset Appreciation' ||
                          field.name === 'Total Projected Gross Yield'
                            ? '%'
                            : field.name === 'Property Value' || field.name === 'Price Per SQM'
                              ? '$'
                              : field.name === 'Area'
                                ? `m\u00B2`
                                : undefined
                        }
                        onChange={(e) => {
                          const newFields = [...propertyFields]
                          const index = newFields.findIndex((f) => f.key === field.key)
                          newFields[index].value = e.target.value
                          setPropertyFields(newFields)
                        }}
                        placeholder={`Enter ${field.name}`}
                      />
                    )}
                  </Form.Item>
                </Col>
              )
            })}
          </Row>
        </fieldset>
        <fieldset>
          <legend>Upload Picture</legend>
          <Row gutter={24}>
            <Col style={{ position: 'relative' }} span={12}>
              {thumbnail.length > 0 && (
                <>
                  <img style={{ width: '50%', border: '1px solid' }} src={thumbnail} alt="" />
                  <CloseOutlined
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: '50%',
                      color: '#000',
                      fontSize: '16px',
                      cursor: 'pointer',
                      background: 'white',
                      borderRadius: '50%',
                      padding: '2px',
                      border: '1px solid',
                    }}
                    onClick={() => {
                      setThumbnail('')
                      setSingleFile([])
                    }}
                  />
                </>
              )}
            </Col>
            <Col span={12}>
              <Row gutter={24}>
                {thumbnails.length > 0 &&
                  thumbnails.map((item, index) => (
                    <Col span={8} key={index} style={{ position: 'relative' }}>
                      <img style={{ width: '100%', border: '1px solid' }} src={item} alt="" />
                      <CloseOutlined
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 14,
                          color: '#000',
                          fontSize: '16px',
                          cursor: 'pointer',
                          background: 'white',
                          borderRadius: '50%',
                          padding: '2px',
                          border: '1px solid',
                        }}
                        onClick={() => handleRemoveImage(index)}
                      />
                    </Col>
                  ))}
              </Row>
            </Col>
            <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
              <div style={{ marginBottom: 10 }}>
                <span style={{ color: 'red' }}>*</span> Upload new avatar{' '}
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your P2P lending avatar. Please upload your chosen image.
                      </p>
                    </div>
                  }
                  placement="right"
                  overlayInnerStyle={{
                    backgroundColor: '#ffffff',
                  }}
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </div>
              <Form.Item name="uploadSingle">
                <Upload
                  fileList={singleFile}
                  customRequest={handleUploadSingle}
                  beforeUpload={beforeUploadSingle}
                >
                  <Button icon={<UploadOutlined />}>Upload Single File</Button>
                </Upload>
              </Form.Item>
              <p>You can only upload JPG/PNG file.</p>
              <p>The image should have the size of 858x423 px.</p>
            </Col>
            <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
              <div style={{ marginBottom: 10 }}>
                <span style={{ color: 'red' }}>*</span> Upload new thumbnails{' '}
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your P2P lending background thumbnails list. Please upload your
                        chosen images.
                      </p>
                    </div>
                  }
                  placement="right"
                  overlayInnerStyle={{
                    backgroundColor: '#ffffff',
                  }}
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </div>
              <Form.Item name="upLloadMulti">
                <Upload listType="text" beforeUpload={beforeUploadMulti} {...props}>
                  <Button icon={<UploadOutlined />}>Upload Multiple Files</Button>
                </Upload>
                <Button
                  type="primary"
                  onClick={handleUploadMulti}
                  disabled={fileList.length === 0}
                  loading={uploading}
                  style={{ marginTop: 16 }}
                >
                  {uploading ? 'Uploading' : 'Start Upload'}
                </Button>
              </Form.Item>

              <p>You can only upload JPG/PNG file.</p>
              <p>The image should have the size of 1024x500 px.</p>
            </Col>
          </Row>
        </fieldset>
        <div style={{ textAlign: 'center', paddingBottom: 5 }}>
          <Button style={{ marginRight: 15 }} key="back" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            disabled={thumbnail.length === 0 || thumbnails.length === 0}
            key="submit"
            type="primary"
            htmlType="submit"
            // onClick={handleOk}
          >
            Create
          </Button>
        </div>
      </Form>
    </div>
  )
}
